<script setup>
import { ref, onMounted, nextTick, watch, computed } from 'vue'
import ImageUploader from '../components/ImageUploader.vue'
import Quill from '../utils/quill'
import { api } from '../api'
import AdminNav from '../components/AdminNav.vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const id = route.params.id
const isNew = id === 'new'
const form = ref({
  slug: '',
  name: '',
  role: '',
  short_bio: '',
  full_bio: '',
  image_url: '',
  order: 0,
  translations: { en: { name: '', role: '', short_bio: '', full_bio: '' } }
})
const uploading = ref(false)
const loading = ref(!isNew)
const saving = ref(false)
const error = ref('')
const savedFlash = ref(false)
const errorFlash = ref(false)
const errorMessage = ref('')

// Rich text editor state
const activeLangTab = ref('el')
const editorContainer = ref(null)
let quill
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  ['clean']
]
const previewHTML = computed(() =>
  activeLangTab.value === 'el' ? form.value.full_bio : form.value.translations.en.full_bio
)

function ensureStructure() {
  if (!form.value.translations) form.value.translations = {}
  if (!form.value.translations.en) form.value.translations.en = {}
  ;['name', 'role', 'short_bio', 'full_bio'].forEach((k) => {
    form.value.translations.en[k] = form.value.translations.en[k] || ''
  })
}

async function load() {
  if (isNew) return
  try {
    const { data } = await api.get('/members')
    const m = data.find((x) => x.id === Number(id))
    if (!m) {
      error.value = 'not_found'
      return
    }
    form.value = {
      ...m,
      translations: {
        en: { name: '', role: '', short_bio: '', full_bio: '', ...(m.translations?.en || {}) }
      }
    }
    if (!form.value.socials)
      form.value.socials = { facebook: '', instagram: '', youtube: '', twitter: '', tiktok: '' }
  } catch {
    error.value = 'load'
  } finally {
    ensureStructure()
    loading.value = false
    nextTick(() => initQuillIfNeeded())
  }
}

async function save() {
  if (quill) {
    const html = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML
    if (activeLangTab.value === 'el') form.value.full_bio = html
    else form.value.translations.en.full_bio = html
  }

  saving.value = true
  error.value = ''
  errorFlash.value = false
  errorMessage.value = ''
  ensureStructure()
  try {
    if (isNew) {
      const { data } = await api.post('/members', form.value)
      router.replace('/admin/members/' + data.id)
    } else {
      await api.put('/members/' + id, form.value)
    }
    savedFlash.value = true
    setTimeout(() => {
      savedFlash.value = false
      router.push('/admin/members')
    }, 1500)
  } catch (e) {
    error.value = 'save'
    errorFlash.value = true
    errorMessage.value = e.response?.data?.detail || t('common.saveFailed')
    setTimeout(() => {
      errorFlash.value = false
      errorMessage.value = ''
    }, 5000)
  } finally {
    saving.value = false
  }
}

async function onImageUpload(blob) {
  uploading.value = true
  errorFlash.value = false
  try {
    const fd = new FormData()
    fd.append('file', blob, (form.value.slug || 'member') + '.jpg')
    if (!isNew) {
      const { data } = await api.post(`/members/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      // Use the image_url returned from the backend
      form.value.image_url = data.image_url || `/api/members/${id}/image`
    } else {
      const { data } = await api.post('/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      form.value.image_url = data.url
    }
    savedFlash.value = true
    setTimeout(() => (savedFlash.value = false), 2000)
  } catch (e) {
    console.error('Upload error', e)
    errorFlash.value = true
    errorMessage.value = e?.response?.data?.detail || e.message || t('common.uploadFailed')
    setTimeout(() => {
      errorFlash.value = false
      errorMessage.value = ''
    }, 5000)
  } finally {
    uploading.value = false
  }
}

function handleUploadError(msg) {
  errorFlash.value = true
  errorMessage.value = msg || t('common.uploadFailed')
  setTimeout(() => {
    errorFlash.value = false
    errorMessage.value = ''
  }, 5000)
}

function loadCurrentContentIntoEditor() {
  if (!quill) return
  const html =
    activeLangTab.value === 'el' ? form.value.full_bio : form.value.translations.en.full_bio
  quill.setContents([])
  quill.clipboard.dangerouslyPasteHTML(html || '')
}
watch(activeLangTab, () => loadCurrentContentIntoEditor())

async function initQuillIfNeeded() {
  if (quill || loading.value) return
  await nextTick()
  const el = editorContainer.value
  if (!el) return
  quill = new Quill(el, {
    theme: 'snow',
    modules: {
      toolbar: toolbarOptions,
      history: { delay: 500, maxStack: 200, userOnly: true },
      blotFormatter: {}
    }
  })
  quill.on('text-change', () => {
    const html = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML
    if (activeLangTab.value === 'el') form.value.full_bio = html
    else form.value.translations.en.full_bio = html
  })
  loadCurrentContentIntoEditor()
}

const gallery = ref([])
async function loadGallery() {
  if (isNew) return
  try {
    if (!form.value.slug) {
      return
    }
    const { data } = await api.get('/members/' + form.value.slug)
    gallery.value = data.gallery || []
  } catch (e) {
    console.warn('gallery load', e)
  }
}
watch(
  () => form.value.slug,
  () => loadGallery()
)
async function uploadGallery(e) {
  const files = Array.from(e.target.files || [])
  errorFlash.value = false
  for (const file of files) {
    if (file.size > 5 * 1024 * 1024) {
      errorFlash.value = true
      errorMessage.value = t('common.fileTooLarge', { max: '5MB' })
      setTimeout(() => {
        errorFlash.value = false
        errorMessage.value = ''
      }, 3000)
      continue
    }
    const fd = new FormData()
    fd.append('file', file, file.name)
    try {
      const { data } = await api.post(`/members/${id}/gallery`, fd)
      gallery.value.push(data)
    } catch (err) {
      errorFlash.value = true
      errorMessage.value = t('common.uploadFailed')
      setTimeout(() => {
        errorFlash.value = false
        errorMessage.value = ''
      }, 3000)
    }
  }
  e.target.value = ''
  if (!errorFlash.value && files.length > 0) {
    savedFlash.value = true
    setTimeout(() => (savedFlash.value = false), 2000)
  }
}
function dragGalleryStart(ev, index) {
  ev.dataTransfer.setData('text/plain', index)
}
function dragGalleryOver(ev) {
  ev.preventDefault()
}
async function dropGallery(ev, index) {
  ev.preventDefault()
  const from = Number(ev.dataTransfer.getData('text/plain'))
  if (isNaN(from) || from === index) return
  const arr = [...gallery.value]
  const [img] = arr.splice(from, 1)
  arr.splice(index, 0, img)
  gallery.value = arr
  try {
    await api.post(`/members/${id}/gallery/reorder`, { order: gallery.value.map((g) => g.id) })
  } catch {
    /* ignore */
  }
}
async function removeGalleryImage(img) {
  if (!confirm(t('admin.confirmDeleteImage'))) return
  try {
    const { data } = await api.delete('/members/images/' + img.id)
    gallery.value = gallery.value.filter((g) => g.id !== img.id)

    // Update profile image if backend changed it
    if (data.new_image_url !== undefined) {
      form.value.image_url = data.new_image_url
    }

    savedFlash.value = true
    setTimeout(() => (savedFlash.value = false), 2000)
  } catch {
    errorFlash.value = true
    errorMessage.value = t('common.deleteFailed')
    setTimeout(() => {
      errorFlash.value = false
      errorMessage.value = ''
    }, 3000)
  }
}

async function setAsPrimaryImage(img) {
  try {
    const { data } = await api.post(`/members/${id}/set-primary-image`, {
      image_id: img.id
    })
    form.value.image_url = data.image_url || img.url
    savedFlash.value = true
    setTimeout(() => (savedFlash.value = false), 2000)
  } catch (e) {
    errorFlash.value = true
    errorMessage.value = e?.response?.data?.detail || t('common.saveFailed')
    setTimeout(() => {
      errorFlash.value = false
      errorMessage.value = ''
    }, 3000)
  }
}

function handleImageError(event, img) {
  // Image failed to load (404 or other error)
  console.warn('Failed to load image:', img.url)
  // Remove broken image from gallery display
  gallery.value = gallery.value.filter((g) => g.id !== img.id)

  // Clear profile image if it points to this broken image
  if (form.value.image_url === img.url) {
    form.value.image_url = ''
  }

  errorFlash.value = true
  errorMessage.value = t('admin.brokenImageRemoved')
  setTimeout(() => {
    errorFlash.value = false
    errorMessage.value = ''
  }, 3000)
}

onMounted(() => {
  if (!isNew) load()
  else {
    loading.value = false
    nextTick(() => initQuillIfNeeded())
  }
  loadGallery()
})
// Auto-generate slug from Greek name (basic transliteration fallback)
watch(
  () => form.value.name,
  (val) => {
    if (!isNew) return
    if (!val) return
    const basic = val
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
    if (!form.value.slug || form.value.slug.startsWith('temp-')) form.value.slug = basic
  }
)
if (!form.value.socials)
  form.value.socials = { facebook: '', instagram: '', youtube: '', twitter: '', tiktok: '' }
</script>
<template>
  <section>
    <div class="admin-nav-wrapper">
      <AdminNav />
    </div>

    <div class="header sticky-header">
      <h2>{{ isNew ? t('admin.memberNew') : t('admin.memberEdit') }}</h2>
      <div class="actions">
        <button
          type="button"
          class="btn"
          @click="router.push('/admin/members')"
          style="margin-right: 0.5rem"
        >
          {{ t('common.cancel') }}
        </button>
        <button type="submit" form="member-form" class="btn-primary" :disabled="saving">
          {{ saving ? t('common.saving') + '...' : '💾 ' + t('common.save') }}
        </button>
      </div>
    </div>

    <p v-if="loading">{{ t('common.loading') }}</p>
    <p v-else-if="error" class="err">
      {{ t(error === 'not_found' ? 'common.notFound' : 'common.errorLoading') }}
    </p>

    <div v-else class="edit-layout">
      <!-- Main Form Section -->
      <div class="main-content">
        <form id="member-form" @submit.prevent="save" class="form">
          <!-- Basic Info Section -->
          <div class="form-section">
            <h3 class="section-title">👤 {{ t('common.name') }} & {{ t('admin.slug') }}</h3>
            <div class="grid">
              <label
                >{{ t('admin.slug') }}<input v-model="form.slug" :disabled="!isNew" required
              /></label>
              <label>{{ t('common.name') }}<input v-model="form.name" required /></label>
              <label>{{ t('common.role') }}<input v-model="form.role" /></label>
              <label
                >{{ t('admin.order') }}<input type="number" v-model.number="form.order" min="0"
              /></label>
            </div>
          </div>

          <!-- Short Bio Section -->
          <div class="form-section">
            <h3 class="section-title">📝 {{ t('admin.shortBio') }}</h3>
            <label
              >{{ t('admin.shortBio') }}<textarea v-model="form.short_bio" rows="4"></textarea>
            </label>
          </div>

          <!-- Full Bio Rich Editor Section -->
          <div class="form-section">
            <h3 class="section-title">
              📄 {{ t('common.description') }} ({{ t('admin.enTranslation') }})
            </h3>
            <div class="rich-bio-block">
              <div class="tab-bar">
                <button
                  type="button"
                  :class="{ active: activeLangTab === 'el' }"
                  @click="activeLangTab = 'el'"
                >
                  EL
                </button>
                <button
                  type="button"
                  :class="{ active: activeLangTab === 'en' }"
                  @click="activeLangTab = 'en'"
                >
                  EN
                </button>
              </div>
              <div class="editor-layout">
                <div class="editor-pane">
                  <div ref="editorContainer" class="quill-wrapper"></div>
                </div>
                <div class="preview-pane" :data-label="t('common.preview')">
                  <div class="preview-inner" v-html="previewHTML"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- English Translation Section -->
          <div class="form-section">
            <h3 class="section-title">🌐 {{ t('admin.enTranslation') }}</h3>
            <div class="grid">
              <label
                >{{ t('common.name') }} (EN)<input v-model="form.translations.en.name"
              /></label>
              <label
                >{{ t('common.role') }} (EN)<input v-model="form.translations.en.role"
              /></label>
            </div>
            <label
              >{{ t('admin.shortBio') }} (EN)<textarea
                v-model="form.translations.en.short_bio"
                rows="4"
              ></textarea>
            </label>
          </div>

          <!-- Social Media Section -->
          <div class="form-section">
            <h3 class="section-title">🔗 {{ t('admin.socialMedia') }}</h3>
            <div class="grid">
              <label>
                {{ t('admin.facebook')
                }}<input v-model="form.socials.facebook" placeholder="https://facebook.com/..." />
              </label>
              <label>
                {{ t('admin.instagram')
                }}<input v-model="form.socials.instagram" placeholder="https://instagram.com/..." />
              </label>
              <label>
                {{ t('admin.youtube')
                }}<input v-model="form.socials.youtube" placeholder="https://youtube.com/..." />
              </label>
              <label>
                {{ t('admin.youtube')
                }}<input v-model="form.socials.twitter" placeholder="https://twitter.com/..." />
              </label>
              <label>
                TikTok<input v-model="form.socials.tiktok" placeholder="https://tiktok.com/..." />
              </label>
            </div>
          </div>

          <!-- External Image URL Section -->
          <div class="form-section">
            <h3 class="section-title">
              🔗 {{ t('admin.imageUrl') }} ({{ t('common.urlPlaceholder') }})
            </h3>
            <label
              >{{ t('admin.imageUrl')
              }}<input v-model="form.image_url" :placeholder="t('common.urlPlaceholder')"
            /></label>
          </div>
        </form>
      </div>

      <!-- Media Upload Sidebar -->
      <div class="media-sidebar">
        <!-- Profile Image Upload Card -->
        <div class="upload-card image-uploader">
          <h3>🖼️ {{ t('admin.profileImage') }}</h3>
          <p class="hint">{{ t('admin.profileImageHint') }}</p>
          <ImageUploader
            :currentImage="form.image_url"
            :uploading="uploading"
            :slug="form.slug || 'member'"
            :initialAspectRatio="1"
            @upload="onImageUpload"
            @error="handleUploadError"
          />
        </div>

        <!-- Gallery Upload Card -->
        <div class="upload-card gallery-manager">
          <h3>
            🎨 {{ t('admin.memberGallery') }}
            <small v-if="gallery.length">({{ gallery.length }})</small>
          </h3>

          <p v-if="isNew" class="hint warning">⚠️ {{ t('admin.saveFirst') }}</p>

          <template v-else>
            <p class="hint">💡 {{ t('admin.galleryHint') }}</p>
            <p v-if="gallery.length" class="hint">
              {{ t('admin.galleryReorderHint') }}
            </p>

            <input
              type="file"
              accept="image/*"
              multiple
              @change="uploadGallery"
              class="file-input"
              id="gallery-upload"
            />
            <label for="gallery-upload" class="upload-label">
              ➕ {{ t('admin.uploadGalleryImages') }}
            </label>

            <div class="gallery-preview" v-if="gallery.length">
              <div
                v-for="(img, index) in gallery"
                :key="img.id"
                class="gallery-item"
                :class="{ 'is-primary': form.image_url === img.url }"
                draggable
                @dragstart="dragGalleryStart($event, index)"
                @dragover="dragGalleryOver"
                @drop="dropGallery($event, index)"
              >
                <img :src="img.url" alt="gallery image" @error="handleImageError($event, img)" />
                <div class="gallery-actions">
                  <button
                    type="button"
                    class="set-primary"
                    @click="setAsPrimaryImage(img)"
                    :title="t('admin.setAsPrimary')"
                    v-if="form.image_url !== img.url"
                  >
                    ⭐
                  </button>
                  <span v-else class="primary-badge" :title="t('admin.primaryImage')">⭐</span>
                  <button
                    type="button"
                    class="delete"
                    @click="removeGalleryImage(img)"
                    :title="t('common.delete')"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            <p v-else class="hint">{{ t('common.empty') }}</p>
          </template>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="savedFlash" class="flash success">✓ {{ t('common.saved') }}</div>
    </transition>
    <transition name="fade">
      <div v-if="errorFlash" class="flash error">
        ✗ {{ errorMessage || t('common.saveFailed') }}
      </div>
    </transition>
  </section>
</template>
<style scoped>
.header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.header h2 {
  margin: 0;
}

.err {
  color: #ef4444;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
}

.edit-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;
}

@media (max-width: 1200px) {
  .edit-layout {
    grid-template-columns: 1fr;
  }
}

.main-content {
  min-width: 0;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: var(--inner);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}

.section-title {
  margin: 0 0 1rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  opacity: 0.9;
  font-weight: 600;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.75rem;
}

.grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  gap: 4px;
}

input,
textarea {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.55rem 0.6rem;
  font: inherit;
  font-size: 0.75rem;
  color: #fff;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: rgb(224, 126, 39);
}

textarea {
  resize: vertical;
}

.actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

button {
  background: rgb(224, 126, 39);
  color: #000;
  border: 1px solid rgb(224, 126, 39);
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

button:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 126, 39, 0.3);
}

button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn {
  background: transparent;
  color: #ddd;
  border: 1px solid var(--border);
}

.btn:hover {
  background: rgba(255, 255, 255, 0.05);
  box-shadow: none;
}

/* Media Sidebar */
.media-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
}

.media-sidebar::-webkit-scrollbar {
  width: 6px;
}

.media-sidebar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.media-sidebar::-webkit-scrollbar-thumb {
  background: rgba(224, 126, 39, 0.5);
  border-radius: 3px;
}

.media-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(224, 126, 39, 0.7);
}

@media (max-width: 1200px) {
  .media-sidebar {
    position: static;
  }
}

.upload-card {
  background: var(--inner);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
}

.upload-card h3 {
  margin: 0 0 1rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
}

.upload-card h3 small {
  font-weight: normal;
  opacity: 0.6;
  margin-left: 0.5rem;
}

.file-input {
  display: none;
}

.upload-label {
  display: inline-block;
  background: rgb(224, 126, 39);
  color: #000;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  margin-bottom: 1rem;
}

.upload-label:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 126, 39, 0.3);
}

.image-editor-container {
  margin-top: 1rem;
}

/* Fix for cropper container sizing */
.image-editor-container .cropper-container {
  width: 100% !important;
}

.aspect-ratio-controls {
  margin-bottom: 1rem;
}

.aspect-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.aspect-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.aspect-btn {
  padding: 0.4rem 0.7rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  font-weight: 500;
}

.aspect-btn:hover {
  background: rgba(224, 126, 39, 0.2);
  border-color: rgb(224, 126, 39);
}

.aspect-btn.active {
  background: rgb(224, 126, 39);
  color: #000;
  border-color: rgb(224, 126, 39);
  font-weight: 600;
}

.cropper-wrapper {
  max-width: 100%;
  max-height: 400px;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  margin-bottom: 1rem;
  position: relative;
}

.cropper-wrapper img {
  max-width: 100%;
  max-height: 400px;
  display: block;
  width: auto;
  height: auto;
}

.cropper-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.control-label {
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.7;
  margin-right: 0.2rem;
}

.control-btn {
  padding: 0.4rem 0.6rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  min-width: 36px;
}

.control-btn:hover {
  background: rgba(224, 126, 39, 0.2);
  border-color: rgb(224, 126, 39);
  transform: translateY(-1px);
}

.control-btn:active {
  transform: translateY(0);
}

.upload-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.crop-btn {
  flex: 1;
  background: rgb(224, 126, 39);
  color: #000;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.crop-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 126, 39, 0.3);
}

.crop-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.7rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  font-size: 0.75rem;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.current-image-preview {
  margin-top: 1rem;
}

.current-image-preview img {
  max-width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  display: block;
}

.gallery-preview {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
}

.gallery-item {
  position: relative;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1;
  cursor: move;
  transition: transform 0.2s;
}

.gallery-item:hover {
  transform: scale(1.05);
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery-item.is-primary {
  border: 3px solid #e07e27;
  box-shadow: 0 0 0 2px rgba(224, 126, 39, 0.2);
}

.gallery-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  align-items: center;
}

.gallery-item .delete,
.gallery-item .set-primary {
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
}

.gallery-item .set-primary {
  font-size: 0.9rem;
}

.gallery-item .set-primary:hover {
  background: rgba(224, 126, 39, 0.9);
  transform: scale(1.1);
}

.gallery-item .delete:hover {
  background: rgba(239, 68, 68, 0.9);
  transform: scale(1.1);
}

.primary-badge {
  background: rgba(224, 126, 39, 0.9);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  font-size: 0.9rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.hint {
  font-size: 0.65rem;
  opacity: 0.6;
  margin: 0.5rem 0;
  line-height: 1.4;
}

.hint.warning {
  color: rgb(224, 126, 39);
  opacity: 0.9;
  font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Rich text editor styles */
.rich-bio-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tab-bar {
  display: flex;
  gap: 0.5rem;
}

.tab-bar button {
  background: rgba(255, 255, 255, 0.05);
  color: #ddd;
  padding: 0.45rem 1rem;
  border: 1px solid var(--border);
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}

.tab-bar button.active {
  background: rgb(224, 126, 39);
  color: #000;
  border-color: rgb(224, 126, 39);
}

.editor-layout {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.editor-pane {
  flex: 1 1 480px;
  min-width: 300px;
}

.quill-wrapper {
  height: 360px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.quill-wrapper :deep(.ql-toolbar) {
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-bottom: 1px solid var(--border);
}

.quill-wrapper :deep(.ql-container) {
  flex: 1;
  background: rgba(255, 255, 255, 0.02);
  border: none;
  color: #eee;
}

.quill-wrapper :deep(.ql-editor) {
  min-height: 260px;
}

.preview-pane {
  flex: 1 1 420px;
  min-width: 260px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  max-height: 360px;
  overflow: auto;
  position: relative;
}

.preview-pane::before {
  content: attr(data-label);
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 0.6rem;
  letter-spacing: 0.5px;
  opacity: 0.6;
  text-transform: uppercase;
}

.preview-inner {
  padding: 1rem;
  font-size: 0.85rem;
  line-height: 1.6;
}

.preview-inner :deep(h1),
.preview-inner :deep(h2),
.preview-inner :deep(h3) {
  margin-top: 1.1em;
  margin-bottom: 0.5em;
}
</style>
